import { ExchanegeRateService } from '@acme/api';
import { Icon } from '@acme/assets';
import {
  ExchangeRateFilter,
  PagedClientExchangeRateResponse,
} from '@acme/domains';
import { Separator, useModal } from '@acme/foundation';
import {
  Button,
  Spinner,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeadCell,
  TableRow,
} from 'flowbite-react';
import { useEffect, useState } from 'react';
import { HiX } from 'react-icons/hi';
import { useNavigate } from 'react-router-dom';
import { formatDate } from '../lib';
import { AdminActionMenu } from './adminActionMenu';
import { EFilterMode, Filter } from './filter';
import { NoData } from './noData/noData';
import { RateHistoryTable } from './rateHistoryTable';

interface IAdminRateTableProps {
  loader: (
    filter: ExchangeRateFilter
  ) => Promise<PagedClientExchangeRateResponse | undefined>;
  loading?: boolean;
  onRowClick?: (currencyId: string) => void;
  filterMode?: EExchangeTableState;
}

enum EExchangeTableState {
  Buy = 1,
  Sell,
}

export const AdminRateTable = ({
  loading,
  loader,
  onRowClick,
  filterMode,
}: IAdminRateTableProps) => {
  const { openModal, closeModal } = useModal();

  const [tableState, setTableState] = useState(
    filterMode || EExchangeTableState.Buy
  );
  const [data, setData] = useState<PagedClientExchangeRateResponse | null>();
  const [filter, setFilter] = useState({});

  const navigate = useNavigate();

  useEffect(() => {
    const loadItems = async () => {
      const response = await loader({
        ...filter,
        type: tableState === EExchangeTableState.Buy ? 100 : 200,
      });
      setData(response);
    };
    loadItems();
  }, [filter, tableState]);

  const loadRateHistory = async (rateId: string) => {
    const res = await ExchanegeRateService.getApiV1ExchanegeRateHistory({
      rateId,
    });
    return res.data;
  };

  const onRateStoryClick = async (
    rateId: string | null | undefined,
    currencyPair: string
  ) => {
    const ratesHistory = await loadRateHistory(rateId as string);
    openModal({
      content: (
        <>
          <div className="flex pb-5">
            <h3 className="text-gray-900 text-lg font-semibold">
              {currencyPair}
            </h3>
            <button
              onClick={closeModal}
              className="text-gray-400 hover:text-gray-600 ml-auto"
            >
              <HiX className="h-5 w-5" />
            </button>
          </div>
          <Separator className="-mx-5" />
          <div className="overflow-auto max-h-[387px] ">
            <RateHistoryTable data={ratesHistory ?? []} adminMode />
          </div>
        </>
      ),
      size: '6xl',
      modalType: 'custom',
    });
  };

  if (loading) {
    return (
      <div className="bg-white rounded-lg shadow-md p-6">
        <div className="flex items-center justify-center py-8">
          <Spinner size="lg" />
          <span className="ml-3 text-gray-600">Загрузка курсов валют...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow-md overflow-hidden">
      <div className="flex flex-col px-4">
        <div className="flex items-center gap-4 border-b border-gray-200 py-4">
          <Button
            color="primary"
            outline
            onClick={() => navigate('/rates/currencyList')}
          >
            Справочник валют
          </Button>
        </div>
        <Filter mode={EFilterMode.ALL_FIELDS} onChange={setFilter} />
      </div>

      {data?.items?.length === 0 ? (
        <NoData
          title={
            Object.keys(filter).length === 0
              ? 'Курсы пока не установлены'
              : 'Курсы по заданным условиям не найдены'
          }
        />
      ) : (
        <div className="overflow-x-auto">
          <Table hoverable>
            <TableHead>
              <TableRow>
                <TableHeadCell></TableHeadCell>
                <TableHeadCell></TableHeadCell>
                <TableHeadCell>Наименование контрагента</TableHeadCell>
                <TableHeadCell>Валютная пара</TableHeadCell>
                <TableHeadCell>Объем валюты</TableHeadCell>
                <TableHeadCell>Объем</TableHeadCell>
                <TableHeadCell>Диапазон объема</TableHeadCell>
                <TableHeadCell>Время обновления</TableHeadCell>
                <TableHeadCell>Количество успешных сделок</TableHeadCell>
              </TableRow>
            </TableHead>

            <TableBody className="divide-y">
              {data?.items?.map((rate) => (
                <TableRow
                  key={rate.rangeId}
                  onClick={() => onRowClick?.(rate.rangeId || '')}
                  className="text-sm font-normal text-gray-900 dark:text-white"
                >
                  <>
                    <TableCell className="w-[60px] text-gray-500">
                      <button
                        onClick={() =>
                          onRateStoryClick(
                            rate.rateId,
                            `${rate.cryptoCurrency?.code} > ${rate.currency?.code}`
                          )
                        }
                      >
                        <Icon name="clock-outline" />
                      </button>
                    </TableCell>
                    <TableCell className="w-[60px] flex px-0 flex-wrap justify-center text-gray-500 hover:text-gray-900">
                      <AdminActionMenu rowData={rate} />
                    </TableCell>
                  </>
                  <TableCell className="whitespace-nowrap cursor-pointer hover:underline">
                    {rate.organization?.name}
                  </TableCell>
                  <TableCell className="whitespace-nowrap">
                    {`${rate.cryptoCurrency?.code} > ${rate.currency?.code}`}
                  </TableCell>
                  <TableCell className="whitespace-nowrap">
                    {`${rate.amount} ${rate.cryptoCurrency?.code}`}
                  </TableCell>
                  <TableCell className="whitespace-nowrap">
                    {`${rate.price} ${rate.currency?.code}`}
                  </TableCell>
                  <TableCell className="whitespace-nowrap">
                    {`От ${rate.rangeMin} до ${rate.rangeMax}`}
                  </TableCell>
                  <TableCell className=" whitespace-nowrap">
                    {formatDate(rate.updatedAt)}
                  </TableCell>
                  <TableCell className="whitespace-nowrap">
                    {rate.dealsCount}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}
    </div>
  );
};
